import React, { useMemo, useRef, useState } from 'react'
import { useFormik } from 'formik'
import {
  ButtonType,
  IconPaths,
  IconSize,
  ID,
  InputType,
  Permission,
  showError,
} from '../../../../../../../_metronic/helpers'
import {
  createCampo,
  createCampoOfEmpresa,
  createOpcao,
  createOpcaoOfEmpresa,
  deleteOpcoes,
  deleteOpcoesOfEmpresa,
  updateCampo,
  updateCampoOfEmpresa,
  updateOpcao,
  updateOpcaoOfEmpresa,
} from '../../core/_requests'
import {
  Button,
  ButtonIcon,
  CustomSelect,
  ErrorLabel,
  FormInput,
  FormTextArea,
  FormTextEditor,
  FormToggle,
  Input,
  Label,
  LoadingIndicator,
  useModal,
} from '../../../../../../components'
import { criarCamposSchema } from './validation/camposSchema'
import {
  FormularioCampoView,
  FormularioDtoCampoCreate,
  FormularioDtoCampoUpdate,
  FormularioOpcaoList,
} from '../../core/_models'
import { useAuth } from '../../../../../auth'
import { useT } from '_metronic/i18n/Metronici18n'
import { CondicaoEditor } from './condicao/CondicaoEditor'
import { CampoReferenciavel } from './condicao/tiposEditor'
import { CondicaoCatalogo } from '../../../core/condicao/catalogo'
import { CampoEsquema, Condicao } from '../../../core/condicao/tipos'
import {
  esquemaParaExibicao,
  validarCondicaoLocal,
} from '../../../core/condicao/validarCondicaoLocal'
import { ErroPosicionado, posicionarErros } from '../../../core/condicao/caminho'
import { sugerirCodigoOpcao } from '../../../core/condicao/codigoOpcao'
import {
  CODIGO_FORMULARIO_BLOQUEADO,
  ErroFormulario,
  errosDoAtributo,
  extrairErrosFormulario,
} from '../../../core/errosFormulario'

interface FieldConfigModalProps {
  field: FormularioCampoView
  formId: ID
  onUpdate: (config: FormularioCampoView) => void
  /** Campos **anteriores** a este, na ordem do formulário (referências possíveis na condição). */
  camposAnteriores?: CampoReferenciavel[]
  catalogo?: CondicaoCatalogo
  /** Formulário bloqueado: tudo desabilitado e sem Salvar. */
  somenteLeitura?: boolean
  /** Chamado quando o backend devolve 409 FORMULARIO_BLOQUEADO durante o salvar. */
  onBloqueado?: (mensagem: string) => void
}

const CATALOGO_VAZIO: CondicaoCatalogo = { variaveis: [], sinais: [], operadores: [] }

export const FieldConfigModal: React.FC<FieldConfigModalProps> = ({
  field,
  onUpdate,
  formId,
  camposAnteriores = [],
  catalogo = CATALOGO_VAZIO,
  somenteLeitura = false,
  onBloqueado,
}) => {
  const { closeModal } = useModal()
  const { currentUser } = useAuth()
  const { t } = useT()

  const [nextOrdem, setNextOrdem] = useState<number>(() => {
    const maxOrdem = field.opcoes?.reduce((max, option) => Math.max(max, option.ordem || 0), 0) ?? 0
    return maxOrdem + 1
  })
  const [deletedOptions, setDeletedOptions] = useState<number[]>([])
  const [errosBackend, setErrosBackend] = useState<ErroFormulario[]>([])
  const [condicao, setCondicao] = useState<Condicao | null>(field.condicaoExibicao ?? null)
  /** Ordens das opções cujo código o usuário editou à mão (param de receber sugestão). */
  const codigosEditados = useRef<Set<number>>(new Set())

  const isAdmin = currentUser?.grupo?.nome === Permission.Admin

  const camposSchema = useMemo(() => criarCamposSchema(t), [t])

  /** Esquema local: os campos anteriores + este, para validar a condição como o backend faria. */
  const esquemaLocal: CampoEsquema[] = useMemo(
    () =>
      camposAnteriores
        .filter((campo) => !!campo.codigo)
        .map((campo, indice) => ({
          codigo: campo.codigo,
          tipo: campo.tipo,
          multiplo: campo.multiplo,
          ordem: indice,
          desempate: indice,
          codigosOpcao: campo.opcoes.map((opcao) => opcao.codigo),
        })),
    [camposAnteriores]
  )

  const campoAtual: CampoEsquema = useMemo(
    () => ({
      codigo: field.codigo || '__atual__',
      tipo: 'TEXTO',
      multiplo: false,
      ordem: esquemaLocal.length,
      desempate: esquemaLocal.length,
      codigosOpcao: [],
    }),
    [field.codigo, esquemaLocal.length]
  )

  const formik = useFormik({
    validationSchema: camposSchema,
    initialValues: {
      rotulo: field.rotulo,
      instrucoes: field.instrucoes || '',
      respostaPadrao: field.respostaPadrao || '',
      tipo: field.tipo,
      obrigatorio: field.obrigatorio || false,
      multiplo: field.multiplo || false,
      opcoes: field.opcoes || [],
      ordem: field.ordem,
      codigo: field.codigo || '',
    },
    onSubmit: async (values, { setSubmitting }) => {
      setSubmitting(true)
      setErrosBackend([])
      // Validação local antes de chamar a API: o erro cai na mesma linha em que cairia o 400.
      const errosLocais = condicao
        ? validarCondicaoLocal(condicao, esquemaParaExibicao(esquemaLocal, campoAtual, catalogo))
        : []
      if (errosLocais.length > 0) {
        setErrosBackend(
          errosLocais.map((erro) => ({
            campo: 'condicaoExibicao',
            codigoCampo: values.codigo || null,
            caminho: erro.caminho,
            codigo: erro.codigo,
            mensagem: erro.mensagem,
          }))
        )
        setSubmitting(false)
        return
      }
      try {
        const opcoesDetalhadas = values.opcoes.map((opcao, indice) => ({
          ordem: opcao.ordem ?? indice + 1,
          descricao: opcao.descricao,
          ...(opcao.codigo ? { codigo: opcao.codigo } : {}),
        }))
        const comum = {
          ordem: field.ordem,
          rotulo: values.rotulo,
          respostaPadrao: values.respostaPadrao,
          instrucoes: values.instrucoes,
          tipo: values.tipo,
          obrigatorio: values.obrigatorio,
          multiplo: values.multiplo,
        }
        let id = field.id
        let codigoSalvo = values.codigo

        if (formId) {
          if (field.id) {
            const dto: FormularioDtoCampoUpdate = {
              ...comum,
              opcoes: [],
              codigo: values.codigo ? values.codigo : null,
              condicaoExibicao: condicao,
            }
            const salvo = isAdmin
              ? await updateCampoOfEmpresa(dto, field.id)
              : await updateCampo(dto, field.id)
            codigoSalvo = salvo?.codigo ?? codigoSalvo

            for (const option of values.opcoes) {
              if (option.id) {
                const atualizada = {
                  id: Number(option.id),
                  descricao: option.descricao,
                  ordem: Number(option.ordem),
                  codigo: option.codigo ? option.codigo : null,
                }
                if (isAdmin) await updateOpcaoOfEmpresa(atualizada, field.id)
                else await updateOpcao(atualizada, field.id)
              } else {
                const nova = {
                  descricao: option.descricao,
                  ordem: option.ordem,
                  ...(option.codigo ? { codigo: option.codigo } : {}),
                }
                const createdOption = isAdmin
                  ? await createOpcaoOfEmpresa(nova, field.id)
                  : await createOpcao(nova, field.id)
                option.id = createdOption.id ?? undefined
              }
            }

            if (deletedOptions.length > 0) {
              if (isAdmin) await deleteOpcoesOfEmpresa(deletedOptions, field.id)
              else await deleteOpcoes(deletedOptions, field.id)
            }
          } else {
            const data: FormularioDtoCampoCreate = {
              ...comum,
              opcoesDetalhadas,
              ...(values.codigo ? { codigo: values.codigo } : {}),
              condicaoExibicao: condicao,
            }
            const createdField = isAdmin
              ? await createCampoOfEmpresa(data, formId)
              : await createCampo(data, formId)
            id = createdField.id
            codigoSalvo = createdField.codigo ?? codigoSalvo
          }
        }
        onUpdate({
          ...field,
          ...values,
          codigo: codigoSalvo || undefined,
          condicaoExibicao: condicao,
          id,
        })
        closeModal()
      } catch (error) {
        const estruturado = extrairErrosFormulario(error)
        if (estruturado?.code === CODIGO_FORMULARIO_BLOQUEADO) {
          closeModal()
          onBloqueado?.(estruturado.message)
          return
        }
        if (estruturado?.erros?.length) {
          setErrosBackend(estruturado.erros)
          const erroCodigo = errosDoAtributo(estruturado.erros, 'codigo')[0]
          if (erroCodigo?.mensagem) formik.setFieldError('codigo', erroCodigo.mensagem)
          return
        }
        showError(error)
      } finally {
        setSubmitting(false)
      }
    },
  })

  const desabilitado = somenteLeitura || formik.isSubmitting

  const errosCondicao: ErroPosicionado[] = useMemo(
    () =>
      posicionarErros(
        errosDoAtributo(errosBackend, 'condicaoExibicao').map((erro) => ({
          caminho: erro.caminho,
          codigo: erro.codigo,
          mensagem: erro.mensagem,
        }))
      ),
    [errosBackend]
  )

  const errosDasOpcoes = useMemo(() => errosDoAtributo(errosBackend, 'opcoes'), [errosBackend])

  const mensagemDaOpcao = (option: FormularioOpcaoList): string | undefined => {
    if (!option.codigo) return undefined
    const alvo = option.codigo.toLowerCase()
    return (
      errosDasOpcoes.find((erro) => (erro.mensagem ?? '').toLowerCase().includes(`"${alvo}"`))
        ?.mensagem ?? undefined
    )
  }

  const mensagensDeOpcaoSemDestino = errosDasOpcoes
    .filter(
      (erro) =>
        !formik.values.opcoes.some(
          (option) =>
            option.codigo &&
            (erro.mensagem ?? '').toLowerCase().includes(`"${option.codigo.toLowerCase()}"`)
        )
    )
    .map((erro) => erro.mensagem)

  const addOption = () => {
    formik.setFieldValue('opcoes', [
      ...formik.values.opcoes,
      { descricao: '', ordem: nextOrdem, codigo: '' },
    ])
    setNextOrdem((prevOrdem) => prevOrdem + 1)
  }

  const updateOptions = (value: string, option: FormularioOpcaoList) => {
    const editouCodigo = codigosEditados.current.has(Number(option.ordem))
    const outrosCodigos = formik.values.opcoes
      .filter((op) => op.ordem !== option.ordem)
      .map((op) => op.codigo)
    const updatedOptions = formik.values.opcoes?.map((op) =>
      op.ordem === option.ordem
        ? {
            ...op,
            descricao: value,
            // Opção nova e sem código editado à mão: o código acompanha a descrição.
            codigo: !op.id && !editouCodigo ? sugerirCodigoOpcao(value, outrosCodigos) : op.codigo,
          }
        : op
    )
    if (formik.values.respostaPadrao && formik.values.respostaPadrao === option.descricao)
      formik.setFieldValue('respostaPadrao', value)
    formik.setFieldValue('opcoes', updatedOptions)
  }

  const updateOptionCodigo = (value: string, option: FormularioOpcaoList) => {
    codigosEditados.current.add(Number(option.ordem))
    formik.setFieldValue(
      'opcoes',
      formik.values.opcoes?.map((op) => (op.ordem === option.ordem ? { ...op, codigo: value } : op))
    )
  }

  const removeOption = (option: FormularioOpcaoList) => {
    setDeletedOptions((prev) => (option.id ? [...prev, option.id] : [...prev]))
    const updatedOptions = formik.values.opcoes?.filter((op) => op.ordem !== option.ordem)
    if (formik.values.respostaPadrao === option.descricao)
      formik.setFieldValue('respostaPadrao', '')
    formik.setFieldValue('opcoes', updatedOptions)
  }

  const erroDaOpcao = (indice: number): string | undefined => {
    const erro = formik.errors.opcoes
    if (Array.isArray(erro)) {
      const item = erro[indice] as { codigo?: string } | undefined
      return item?.codigo
    }
    return undefined
  }

  const erroGeralDasOpcoes =
    typeof formik.errors.opcoes === 'string' ? formik.errors.opcoes : undefined

  const renderOptionsDefault = () => {
    const data = formik.values.opcoes?.map((option) => ({
      value: option.descricao,
      label: option.descricao,
    }))
    return (
      <div className='row mb-7'>
        <Label className='pb-2'>Reposta Padrão</Label>
        <CustomSelect
          options={data}
          placeholder='Selecionar reposta padrão'
          onChange={(option) => formik.setFieldValue('respostaPadrao', option ? option.value : '')}
          isDisabled={desabilitado}
          value={
            formik.values.respostaPadrao
              ? { label: formik.values.respostaPadrao, value: formik.values.respostaPadrao }
              : undefined
          }
        />
      </div>
    )
  }

  return (
    <form onSubmit={formik.handleSubmit}>
      <FormInput
        formik={formik}
        name='rotulo'
        label='Rótulo'
        placeholder='Digite a pergunta'
        className='mb-7'
        isRequired
        isDisabled={desabilitado}
      />
      <FormInput
        formik={formik}
        name='codigo'
        label={t('formularios.construtor.codigo.label')}
        placeholder={t('formularios.construtor.codigo.placeholder')}
        className='mb-2'
        isDisabled={desabilitado}
      />
      <p className='text-muted fs-8 mb-7'>{t('formularios.construtor.codigo.ajuda')}</p>
      <FormTextArea
        formik={formik}
        name='instrucoes'
        label='Instruções'
        placeholder='Preencha as instruções'
        className='mb-7'
        isDisabled={desabilitado}
      />

      {formik.values.tipo !== InputType.Radio && formik.values.tipo !== InputType.Select && (
        <>
          {formik.values.tipo === InputType.TextEditor ? (
            <FormTextEditor
              formik={formik}
              name='respostaPadrao'
              label='Resposta Padrão'
              placeholder='Preencha as instruções'
              className='mb-7'
            />
          ) : (
            <FormInput
              formik={formik}
              type={formik.values.tipo === InputType.Number ? 'number' : 'text'}
              name='respostaPadrao'
              label='Resposta Padrão'
              placeholder='Preencha as instruções'
              className='mb-7'
              isDisabled={desabilitado}
            />
          )}
        </>
      )}
      <div className='d-flex flex-end'>
        <FormToggle
          formik={formik}
          name='obrigatorio'
          label='Obrigatório'
          isDisabled={desabilitado}
          className='mb-7'
        />
      </div>

      {formik.values.tipo === InputType.Select && (
        <div className='d-flex flex-end'>
          <FormToggle
            formik={formik}
            name='multiplo'
            label='Permitir múltipla seleção'
            isDisabled={desabilitado}
            className='mb-7'
          />
        </div>
      )}

      {(formik.values.tipo === InputType.Radio || formik.values.tipo === InputType.Select) && (
        <>
          <div className='d-flex fv-row mb-7 align-items-center justify-content-between'>
            <Label className='pb-2'>Opções</Label>
            {!somenteLeitura && (
              <Button
                onClick={addOption}
                className='btn-primary active btn-sm'
                icon={IconPaths.Add}
                disabled={desabilitado}
              >
                Adicionar Opção
              </Button>
            )}
          </div>
          {erroGeralDasOpcoes && <ErrorLabel>{erroGeralDasOpcoes}</ErrorLabel>}
          {mensagensDeOpcaoSemDestino.map((mensagem) => (
            <ErrorLabel key={mensagem}>{mensagem}</ErrorLabel>
          ))}
          {formik.values.opcoes?.map((option: FormularioOpcaoList, index: number) => {
            const mensagemLocal = erroDaOpcao(index)
            const mensagemServidor = mensagemDaOpcao(option)
            return (
              <div key={index} className='row mb-4'>
                <div className='col-7'>
                  <Input
                    value={option.descricao}
                    onChange={(e) => updateOptions(e.target.value, option)}
                    disabled={desabilitado}
                    placeholder='Digite seu campo'
                    name={`opcao-descricao-${index + 1}`}
                    aria-label={t('formularios.construtor.opcao.descricao', { numero: index + 1 })}
                  />
                </div>
                <div className='col-4'>
                  <Input
                    value={option.codigo ?? ''}
                    onChange={(e) => updateOptionCodigo(e.target.value, option)}
                    disabled={desabilitado}
                    placeholder={t('formularios.construtor.opcao.codigo.placeholder')}
                    name={`opcao-codigo-${index + 1}`}
                    aria-label={t('formularios.construtor.opcao.codigo', { numero: index + 1 })}
                    isInvalid={!!mensagemLocal || !!mensagemServidor}
                  />
                </div>
                <div className='col-1 d-flex align-items-center justify-content-center'>
                  {!somenteLeitura && (
                    <ButtonIcon
                      className='p-0 m-0'
                      onClick={() => removeOption(option)}
                      icon={IconPaths.Trash}
                      iconSize={IconSize.Small}
                      disabled={desabilitado}
                      ariaLabel={t('formularios.construtor.opcao.remover', { numero: index + 1 })}
                    />
                  )}
                </div>
                {(mensagemLocal || mensagemServidor) && (
                  <ErrorLabel>{mensagemLocal || mensagemServidor}</ErrorLabel>
                )}
              </div>
            )
          })}
          {renderOptionsDefault()}
        </>
      )}

      <fieldset className='mb-7 border-0 p-0'>
        <legend className='fs-6 fw-bolder'>{t('formularios.condicao.titulo')}</legend>
        <CondicaoEditor
          value={condicao}
          campos={camposAnteriores}
          catalogo={catalogo}
          erros={errosCondicao}
          disabled={desabilitado}
          onChange={setCondicao}
        />
      </fieldset>

      {!somenteLeitura && (
        <div className='d-flex justify-content-end'>
          <Button className='btn-primary' type={ButtonType.Submit} disabled={desabilitado}>
            {formik.isSubmitting ? <LoadingIndicator /> : 'Salvar'}
          </Button>
        </div>
      )}
    </form>
  )
}
